<template>
	<div
		v-if="apiKey"
		class="cassie-vertical-md"
	>
		<SectionCard>
			<template #title>
				Access Key Details
			</template>
			<template #body>
				<v-row dense>
					<v-col cols="6">
						<TextField
							v-model="apiKey.apiKeyName"
							:disabled="!userFullPermissions"
							label="Access Key Name *"
							required="true"
							reserve-tooltip-space
						/>
					</v-col>
					<v-col cols="6">
						<TextField
							v-model="apiKey.apiKeyDescription"
							:disabled="!userFullPermissions"
							label="Description"
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="6">
						<TextField
							v-model="apiKey.tokenExpirationInMinutes"
							:disabled="!userFullPermissions"
							label="Expiry in Minutes *"
							type="number"
							tooltip-text="Input the length of time that your API Access Key 'Bearer Token' is active for, up to a maximum of 1440 minutes (24 Hours), after which you'll need to reauthenticate."
							:rules="{ required: true, numeric: true, betweenMinutes: [1, 1440] }"
						/>
					</v-col>
					<v-col cols="6">
						<v-switch
							v-model="allEndpoints"
							inset
							:disabled="!userFullPermissions"
							label="Has full permissions on all APIs"
							dense
							hide-details
							class="ma-0 mt-1"
						/>
					</v-col>
				</v-row>
				<v-row dense>
					<v-col cols="12">
						<v-switch
							v-model="apiKey.displayDataSubjectsWithNoPreferences"
							inset
							:disabled="!userFullPermissions"
							dense
							hide-details
							class="ma-0 mt-1"
						>
							<template #label>
								<span>
									Display Data Subjects with no Preferences?
								</span>
								<v-tooltip bottom>
									<template #activator="{ on, attrs }">
										<v-icon
											small
											class="ml-2"
											v-bind="attrs"
											v-on="on"
										>
											mdi-information
										</v-icon>
									</template>
									<span>
										When enabled (default), the following TAPI endpoints will return data subject PII even when no preferences exist after brand filtering:
										<div style="padding-left: 16px;padding-top:2px;padding-bottom:4px;">
											• Get Data Subject - Current<br>
											• Get Data Subject - History<br>
											• Get Data Subject - Submissions<br>
										</div>
										When disabled, these endpoints will return 204 No Content instead of PII when the data subject has zero preferences.
									</span>
								</v-tooltip>
							</template>
						</v-switch>
					</v-col>
				</v-row>
			</template>
		</SectionCard>
		<SectionCard>
			<template #title>
				Brand Access
			</template>
			<template #body>
				<div class="d-flex cassie-horizontal-md align-center mb-4">
					<Dropdown
						v-model="selectedBrandId"
						:search-input.sync="brandSearchInput"
						:items="brandOptions"
						:disabled="!userFullPermissions || brandOptions.length === 1"
						custom-sort
						:label="brandLabel"
						searchable
						rules="requiredBrands"
						class="cassie-input-width-md"
					/>
				</div>
			</template>
		</SectionCard>
		<div
			v-if="!allEndpoints"
			:disabled="!userFullPermissions"
			class="d-flex align-center cassie-horizontal-md"
			style="flex: 1;"
		>
			<SecondaryActionButton
				:input-value="showFilters"
				:disabled="!userFullPermissions"
				@click="showFilters = !showFilters"
			>
				<v-icon>
					mdi-filter
				</v-icon>
				Filter Endpoints
			</SecondaryActionButton>
			<TextField
				v-if="showFilters"
				v-model="searchQuery"
				label="Endpoint Name"
				append-icon="mdi-magnify"
			/>
			<Dropdown
				v-if="showFilters"
				v-model="selectedEndpointType"
				label="Endpoint Method"
				:items="endpointMethodFilter"
			/>
		</div>
		<div
			v-show="!allEndpoints"
			:disabled="!userFullPermissions"
			class="cassie-vertical-md"
		>
			<SectionCard
				v-for="api in apis"
				:key="api.apiId"
				collapsible
				:collapsed="true"
			>
				<template #title>
					<span class="text-h6">
						{{ api.apiName }}
					</span>
					<span class="text-subtitle-1 px-1 pt-1">
						{{ ' - ' + api.apiDescription }}
					</span>
				</template>
				<template #body>
					<DataTable
						:headers="tableHeaders"
						:items="filteredEndpoints(api.endpoints)"
						:disabled="!userFullPermissions"
						sort-by="apiEndpointUrl"
						:sort-desc="false"
					>
						<template #item.apiEndpointDisplayName="{item}">
							<span>
								{{ item.apiEndpointDisplayName }}
								<v-tooltip
									v-if="item.apiEndpointTooltip"
									slot="append"
									top
								>
									<template #activator="{ on }">
										<v-icon
											light
											v-on="on"
										>
											mdi-information
										</v-icon>
									</template>
									<span>
										{{ item.apiEndpointTooltip }}
									</span>
								</v-tooltip>
								<Chip
									v-if="isAffectedByDisplayFlag(item.endpointName)"
									:color="apiKey.displayDataSubjectsWithNoPreferences ? 'info' : 'warning'"
									class="ml-2"
								>
									{{ apiKey.displayDataSubjectsWithNoPreferences ? 'Including' : 'Excluding' }} Data Subjects With No Preferences
								</Chip>
							</span>
						</template>
						<template #item.apiEndpointType="{item}">
							<div
								:class="getEndpointColour(item.apiEndpointType)"
								class="endpoint-indicator d-flex align-center justify-center"
							>
								{{ item.apiEndpointType }}
							</div>
						</template>
						<template #item.isActive="{item}">
							<v-switch
								inset
								dense
								:input-value="getValue(item.endpointId,api.apiId)"
								:disabled="!userFullPermissions"
								@change="setApiKeyEndpoints(item.endpointId,api.apiId, $event)"
							/>
						</template>
						<template #item.apiEndpointDocumentationLink="{item}">
							<div style="display: flex; justify-content: center;">
								<v-menu offset-y>
									<template #activator="{ on, attrs }">
										<v-btn
											icon
											small
											v-bind="attrs"
											v-on="on"
											@click.stop
										>
											<v-icon>
												mdi-dots-horizontal
											</v-icon>
										</v-btn>
									</template>
									<v-list dense>
										<v-list-item @click="openDeveloperDocs(item.apiEndpointDocumentationLink)">
											<v-list-item-content>
												<v-list-item-title>
													View Documentation
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</v-list>
								</v-menu>
							</div>
						</template>
					</DataTable>
				</template>
			</SectionCard>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import Chip from '../../../../../../shared/components/chip.vue'
import { brandOptions } from '../../../../../../shared/state/brands.js'
import { getAllEndpoints, updateApiKey, createApiKey } from '../../../../../../shared/utils/api/access-keys.js'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { API_IDS } from '../../../../../../shared/enums/access-keys.js'
import { getEnv } from '../../../../../../shared/utils/runtime-settings.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'

export default {
	components: { SectionCard, TextField, DataTable, SecondaryActionButton, Dropdown, Chip },
	props: {
		apiKeyToEdit: Object,
		isEdit: Boolean,
		userFullPermissions: Boolean
	},
	setup () {
		return {
			brandOptions,
			API_IDS,
			showSnackbar
		}
	},
	data () {
		return {
			apiKey: null, // Initialized in created() lifecycle hook
			selectedBrand: null,
			brandSearchInput: '',
			apis: [],
			sapiUrl: getEnv('VUE_APP_BASE_URL'),
			tapiUrl: getEnv('VUE_APP_BASE_URL_TRANSACTIONAL'),
			consentConfigUrl: getEnv('VUE_APP_BASE_URL_CONSENTCONFIGAPI'),
			standardapiUrl: getEnv('VUE_APP_BASE_URL_STANDARD'),
			sapiName: getEnv('VUE_APP_SAPIAPI_NAME'),
			tapiName: getEnv('VUE_APP_TRANSACTIONALAPI_NAME'),
			consentConfigName: getEnv('VUE_APP_CONSENTCONFIGAPI_NAME'),
			standardApiName: getEnv('VUE_APP_STANDARDAPI_NAME'),
			sapiDescription: getEnv('VUE_APP_SAPIAPI_DESCR'),
			tapiDescription: getEnv('VUE_APP_TRANSACTIONALAPI_DESCR'),
			consentConfigDescription: getEnv('VUE_APP_CONSENTCONFIGAPI_DESCR'),
			standardApiDescription: getEnv('VUE_APP_STANDARDAPI_DESCR'),
			showFilters: false,
			searchQuery: null,
			selectedEndpointType: null,
			allEndpoints: false
		}
	},
	computed: {
		...mapGetters('auth', ['userHasUniversalBrand', 'userBrands']),
		tableHeaders () {
			return [
				{
					text: 'Method',
					value: 'apiEndpointType',
					width: '12%'
				},
				{
					text: 'Name',
					value: 'apiEndpointDisplayName',
					width: '38%'
				},
				{
					text: 'Active',
					value: 'isActive',
					width: '12%'
				},
				{
					text: 'Endpoint URL',
					value: 'apiEndpointUrl',
					width: '38%'
				},
				{
					text: 'Action',
					value: 'apiEndpointDocumentationLink',
					align: 'center',
					width: '80px',
					sortable: false
				}
			]
		},
		endpointMethodFilter () {
			return [
				{
					value: null,
					text: 'View All'
				},
				{
					value: 'GET',
					text: 'GET'
				},
				{
					value: 'POST',
					text: 'POST'
				},
				{
					value: 'PUT',
					text: 'PUT'
				},
				{
					value: 'DELETE',
					text: 'DELETE'
				}
			]
		},
		hasStandardApi () {
			return !!this.apis.find(({ apiId }) => apiId === API_IDS.STANDARDAPI)?.length
		},
		sapiEndpointIds () {
			return this.apis.find(({ apiId }) => apiId === API_IDS.SAPI).endpoints.map(({ endpointId }) => endpointId)
		},
		tapiEndpointIds () {
			return this.apis.find(({ apiId }) => apiId === API_IDS.TAPI).endpoints.map(({ endpointId }) => endpointId)
		},
		standardApiEndpointIds () {
			return this.apis.find(({ apiId }) => apiId === API_IDS.STANDARDAPI).endpoints.map(({ endpointId }) => endpointId)
		},
		consentConfigApiEndpointIds () {
			return (this.apis.find(({ apiId }) => apiId === API_IDS.CONSENTCONFIGAPI)?.endpoints ?? []).map(({ endpointId }) => endpointId)
		},
		selectedBrandId: {
			get () {
				return this.apiKey.brandId ?? null
			},
			set (value) {
				this.apiKey.brandId = value ?? null
			}
		},
		singleBrandOptionExists () {
			// Check if only one brand is available in the system (not just selected) to prevent deletion in single-brand envs
			return brandOptions.value?.length <= 1
		},
		brandLabel () {
			// Apply custom terminology
			const label = useLabels('Brand')
			return brandOptions.value?.length === 1 ? label : `${label} *`
		}
	},
	async created () {
		this.apiKey = JSON.parse(JSON.stringify(this.apiKeyToEdit
			? {
				...this.apiKeyToEdit,
				brandId: this.apiKeyToEdit.brandId != null
					? this.apiKeyToEdit.brandId
					: this.getDefaultBrandId(),
				displayDataSubjectsWithNoPreferences: this.apiKeyToEdit.displayDataSubjectsWithNoPreferences ?? true
			}
			: {
				apiKeyName: '',
				apiKeyDescription: '',
				endpointIds: [],
				standardApiEndpointIds: [],
				brandId: this.getDefaultBrandId(),
				tokenExpirationInMinutes: 20,
				displayDataSubjectsWithNoPreferences: true
			}))
		const { data: { apis } } = await getAllEndpoints()
		this.apis = apis
		this.setApiUrls()
		this.isAllEndpoints()
	},
	methods: {
		getDefaultBrandId () {
			// Universal users get 0, Single-brand users get their single brand, Multi-brand users start with null
			if (this.userHasUniversalBrand) return 0
			if (this.userBrands.length === 1) return this.userBrands[0]
			return null
		},
		setApiUrls () {
			const sapi = this.apis.find(({ apiId }) => apiId === API_IDS.SAPI)
			if (sapi != null) {
				sapi.apiUrl = this.sapiUrl
				sapi.apiDescription = this.sapiDescription
				sapi.apiName = this.sapiName
			}
			const tapi = this.apis.find(({ apiId }) => apiId === API_IDS.TAPI)
			if (tapi != null) {
				tapi.apiUrl = this.tapiUrl
				tapi.apiDescription = this.tapiDescription
				tapi.apiName = this.tapiName
			}
			const consentConfigApi = this.apis.find(({ apiId }) => apiId === API_IDS.CONSENTCONFIGAPI)
			if (consentConfigApi != null) {
				consentConfigApi.apiUrl = this.consentConfigUrl
				consentConfigApi.apiDescription = this.consentConfigDescription
				consentConfigApi.apiName = this.consentConfigName
			}
			const standardapi = this.apis.find(({ apiId }) => apiId === API_IDS.STANDARDAPI)
			if (standardapi != null) {
				standardapi.apiUrl = this.standardapiUrl
				standardapi.apiDescription = this.standardApiDescription
				standardapi.apiName = this.standardApiName
			}
		},
		isAllEndpoints () {
			const allSapiAndTapiEndpoints = [...this.sapiEndpointIds, ...this.tapiEndpointIds]
			if (!this.hasStandardApi) {
				this.allEndpoints = this.apiKey.endpointIds.length === allSapiAndTapiEndpoints.length
			} else {
				this.allEndpoints = this.apiKey.endpointIds.length === allSapiAndTapiEndpoints.length &&
				this.apiKey.standardApiEndpointIds.length === this.standardApiEndpointIds.length
			}
		},
		filteredEndpoints (endpoints) {
			return endpoints.filter(({ apiEndpointDisplayName, apiEndpointType }) => {
				let check = true
				if (this.searchQuery) check = apiEndpointDisplayName.toLowerCase().includes(this.searchQuery.toLowerCase())
				if (this.selectedEndpointType !== null) check = check && apiEndpointType === this.selectedEndpointType
				return check
			})
		},
		getValue (endpointId, apiId) {
			if (apiId === API_IDS.STANDARDAPI) {
				return this.apiKey.standardApiEndpointIds.includes(endpointId)
			}
			return this.apiKey.endpointIds.includes(endpointId)
		},
		setApiKeyEndpoints (endpointId, apiId, value) {
			if (value) {
				if (apiId === API_IDS.STANDARDAPI) {
					this.apiKey.standardApiEndpointIds.push(endpointId)
				} else {
					this.apiKey.endpointIds.push(endpointId)
				}
			} else {
				if (apiId === API_IDS.STANDARDAPI) {
					const index = this.apiKey.standardApiEndpointIds.findIndex(
						endpoint => endpoint === endpointId
					)
					this.apiKey.standardApiEndpointIds.splice(index, 1)
				} else {
					const index = this.apiKey.endpointIds.findIndex(
						endpoint => endpoint === endpointId
					)
					this.apiKey.endpointIds.splice(index, 1)
				}
			}
		},
		isAffectedByDisplayFlag (endpointName) {
			const affected = [
				'OQueryGetDataSubjectCurrentPOST',
				'HighThroughputGetDataSubjectHistoryPOST',
				'HighThroughputGetDataSubjectSubmissionsPOST'
			]
			return affected.includes(endpointName)
		},
		getEndpointColour (endpointType) {
			return { GET: 'endpoint-get', POST: 'endpoint-post', PUT: 'endpoint-put', DELETE: 'endpoint-delete' }[endpointType] || ''
		},
		openDeveloperDocs (apiEndpointDocumentationLink) {
			window.open(apiEndpointDocumentationLink, '_blank')
		},
		async saveApiKey () {
			if (this.allEndpoints) {
				this.apiKey.endpointIds = (this.apis.filter(({ apiId }) => {
					let check = true
					if (apiId === 3) check = false
					return check
				}).map(dataSet1 => dataSet1.endpoints.map(dataSet2 => dataSet2.endpointId)).reduce(function (prev, next) {
					return prev.concat(next)
				}))
				const standardApiEndpointIds = this.apis.filter(({ apiId }) => apiId === 3)
				if (standardApiEndpointIds.length) {
					this.apiKey.standardApiEndpointIds = standardApiEndpointIds.map(dataSet1 => dataSet1.endpoints.map(dataSet2 => dataSet2.endpointId)).reduce(function (prev, next) {
						return prev.concat(next)
					})
				}
			}
			if (!this.allEndpoints && !this.apiKey.endpointIds?.length && !this.apiKey.standardApiEndpointIds?.length) {
				showSnackbar({ color: 'red', text: 'You must add at least 1 endpoint to this API Key' })
				return
			}
			if (this.apiKey.brandId == null) {
				showSnackbar({ color: 'red', text: 'You must select a brand for this API Key' })
				return
			}
			if (!this.isEdit) {
				const newApiKey = {
					apiKeyName: this.apiKey.apiKeyName,
					brandId: this.apiKey.brandId,
					endpointIds: this.apiKey.endpointIds,
					standardApiEndpointIds: this.apiKey.standardApiEndpointIds,
					apiKeyDescription: this.apiKey.apiKeyDescription,
					tokenExpirationInMinutes: this.apiKey.tokenExpirationInMinutes,
					displayDataSubjectsWithNoPreferences: this.apiKey.displayDataSubjectsWithNoPreferences
				}
				await createApiKey(newApiKey)
			} else {
				const updatedApiKey = {
					apiKeyId: this.apiKey.apiKeyId,
					apiKeyName: this.apiKey.apiKeyName,
					brandId: this.apiKey.brandId,
					endpointIds: this.apiKey.endpointIds,
					standardApiEndpointIds: this.apiKey.standardApiEndpointIds,
					apiKeyDescription: this.apiKey.apiKeyDescription,
					apiKey: this.apiKey.apiKey,
					tokenExpirationInMinutes: this.apiKey.tokenExpirationInMinutes,
					displayDataSubjectsWithNoPreferences: this.apiKey.displayDataSubjectsWithNoPreferences
				}
				await updateApiKey(updatedApiKey)
			}
			this.$emit('back-to-overview')
		}
	}
}
</script>
<style scoped>
.endpoint-indicator {
	max-width: 80px;
	height: 24px;
}
</style>
